import React, { useRef, useState } from "react";
/** Assets */
import defaultBackGroundImage from "assets/images/defaultAppBackgroundImage.png";
import { Bars3Icon, PlusIcon } from "@heroicons/react/24/outline";
import HomeIcon from "assets/icons/HeroIcons/HomeIcon";
import { ArrowLeftIcon, BellIcon } from "assets/icons/HeroIcons";
import { CameraIcon } from "assets/icons/HeroIcons";
import usePermissions from "hooks/usePermissions";
import { getCurrentBranding } from "utils/customBranding";
import { brandLogos } from "hooks/useCustomBranding/constants";
import { Toasts } from "view/components/Toasts";
import { MAXIMUM_FILE_SIZE } from "constants/FileSize";
import apiLibrary from "services/api";
import { useDispatch } from "react-redux";
import { setLoadingProgress } from "store/loadingBar";
import { getRandomProgress } from "utils/getRandomProgress";
import WifiFillIcon from "assets/icons/HeroIcons/WifiFillIcon";

interface ImageData {
  title: string;
  value: any | null;
  description: string;
  imageKey: string;
  fileId: number | null;
  filePath: string | null;
  appName: string;
  getUniversalSettings: () => void;
  setSettings: any;
}

export const ObserverOfflineChecklist: React.FC<ImageData> = ({
  title,
  value,
  description,
  imageKey,
  fileId,
  filePath,
  appName,
  getUniversalSettings,
  setSettings,
}) => {
  const dispatch = useDispatch();

  const { universalSettings, universalSettingsImages } = usePermissions();
  const [isLoading, setIsLoading] = useState(false);

  const currentBrand = getCurrentBranding();
  const CURRENT_BRAND_LOGO = brandLogos[currentBrand];

  const fileInputRef = useRef<HTMLInputElement>(null);

  /**
   * Handles the upload of a user's profile image.
   * Accepts only JPEG/JPG/PNG with EXACT dimensions of 1080×1920.
   * @param {React.ChangeEvent<HTMLInputElement>} event - The event triggered by selecting a file for upload.
   */
  const handleImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const input = event.target;
    const file = input.files?.[0];

    const resetInput = () => {
      input.value = "";
    };

    const getImageDimensions = (
      f: File
    ): Promise<{ width: number; height: number }> =>
      new Promise((resolve, reject) => {
        const url = URL.createObjectURL(f);
        const img = new Image();
        img.onload = () => {
          const dims = { width: img.width, height: img.height };
          URL.revokeObjectURL(url);
          resolve(dims);
        };
        img.onerror = () => {
          URL.revokeObjectURL(url);
          reject(new Error("Invalid image file."));
        };
        img.src = url;
      });

    try {
      if (!file) return;


      const validImageTypes = ["image/jpeg", "image/jpg", "image/png"];
      if (!validImageTypes.includes(file.type)) {
        Toasts.error("Please select a valid image file (jpeg/jpg/png).");
        resetInput();
        return;
      }

      // Max size 25 MB
      const fileSizeMB = file.size / (1024 * 1024);
      if (fileSizeMB > 25) {
        Toasts.error("File size must be less than 25 MB!");
        resetInput();
        return;
      }

      // STRICT dimension check (must be exactly 1080×1920)
      const { width, height } = await getImageDimensions(file);
      if (width !== 1080 || height !== 1920) {
        Toasts.error("Please ensure the image is exactly 1080×1920 pixels.");
        resetInput();
        return;
      }
      dispatch(setLoadingProgress(getRandomProgress()));

      setIsLoading(true);

      const uploadResponse = await apiLibrary.file.fileUpload(file);

      const { message } = await apiLibrary.systemSettings.updateUniversalImages(
        imageKey,
        appName,
        uploadResponse.data.id
      );

      Toasts.success(message);
      await getUniversalSettings();
    } catch (error: any) {
      const errorMsg =
        error?.response?.data?.message ??
        error.message ??
        "Something went wrong.";
      Toasts.error(errorMsg);
      resetInput();
    } finally {
      setIsLoading(false);
      dispatch(setLoadingProgress(100));
    }
  };

  /**
   * Handles the click event to trigger the file input element.
   */
  const handleClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };
  const resetToDefault = async () => {
    if (fileId === null) return;
    try {
      setIsLoading(true);
      dispatch(setLoadingProgress(getRandomProgress()));

      const { data, message } =
        await apiLibrary.systemSettings.updateUniversalImages(
          imageKey,
          appName,
          null
        );
      Toasts.success(message);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      getUniversalSettings();
    } catch (error: any) {
      const errorMsg = error?.response?.data?.message ?? error.message;
      Toasts.error(errorMsg);
    } finally {
      setIsLoading(false);
      dispatch(setLoadingProgress(100));
    }
  };
  return (
    <div className="w-full h-[342px] col-span-1">
      <div>
        <div>
          <p className="font-semibold text-secondaryMid dark:text-textMain text-base">
            Observer Offline Checklist
          </p>
          <p className="text-sm text-textMidLight dark:text-textMain">
            For the best results on all devices,
            <br /> use an image that’s 1080 x 1920 px.
          </p>
          <button
            type="button"
            onClick={resetToDefault}
            disabled={isLoading || fileId === null}
            className="text-primary text-xs font-semibold mt-2 cursor-pointer disabled:cursor-not-allowed"
          >
            Reset to default
          </button>
        </div>
        <div className="relative w-[140px] bg-primary_9 h-[265px] rounded-md flex flex-col">
          {/* Background Image */}
          <div
            className="absolute inset-0 bg-cover bg-center h-[265px] rounded-md z-10"
            style={{
              backgroundImage: `url(${filePath ? filePath : defaultBackGroundImage})`,
            }}
          />
          {/* background overlay */}
          <div className="absolute inset-0 bg-[#a19e9e] h-[265px] bg-opacity-50 rounded-md z-10"></div>

          {/* Status Bar */}
          <div className="px-[6px] pt-[1px] pb-1 flex justify-between items-center text-white text-sm relative z-10">
            <span className="text-[7.52px] text-textMidDark font-medium">
              9:30
            </span>
            <span className=" mr-2">
              <WifiFillIcon height={10} width={8} />
            </span>

            {/* Hidden file input */}
            <input
              disabled={isLoading}
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="hidden"
            />
            <button
              onClick={handleClick}
              disabled={isLoading}
              className="h-8 w-8 disabled:cursor-not-allowed flex justify-center items-center rounded-full bg-bgWhite dark:bg-secondaryLight absolute -right-[15px] -top-[15px]"
            >
              <CameraIcon className="w-5 h-5" />
            </button>
          </div>

          <div className="flex items-center w-full relative px-2 ps-3 z-10 gap-1">
            <span>
              <ArrowLeftIcon
                className="w-2 h-2 2 dark:stroke-textWhite"
                fill="#FFFFFF"
              />
            </span>{" "}
            <p className="text-textWhite text-[6px]">Home screen</p>
          </div>

          <div className="relative z-20 top-[50px] px-3">
            <p className="text-textWhite text-[9.5px] py-2 font-semibold">
              Offline Checklist
            </p>
            <div className="bg-bgBluish dark:bg-bgMain h-[105px] flex flex-col justify-evenly gap-1 p-[6px] rounded-[4px] ">
              <div className="h-[21px] bg-bgWhite dark:bg-secondaryLight rounded-[4px]"></div>
              <div className="h-[21px] bg-bgWhite dark:bg-secondaryLight rounded-[4px]"></div>
              <div className="h-[21px] bg-bgWhite dark:bg-secondaryLight rounded-[4px]"></div>
              <div className="h-[21px] bg-bgWhite dark:bg-secondaryLight rounded-[4px]"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
